import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "context/UserContext";
import { useFirebase } from "firebase";
import { useGetIdToken } from "hooks/usePublicAPI";

import { useQuoteFlowAnalytics, type QuoteType } from "./analytics";
import {
  mintQuoteSession,
  requestSignedInSession,
  type QuotePresence,
} from "./api";
import {
  exchangeSignedInSession,
  openWithCookie,
  openWithLink,
  shouldTrySignedInSession,
  signedInExchangeCanHelp,
  type AccessOutcome,
} from "./quote-access";
import { readReentryNotFound, readReentryProof } from "./reentry";

interface QuoteAccessOptions<V> {
  quoteId: string | undefined;
  subject: QuoteType;
  /** The URL's one-time link, read once at mount. */
  linkToken: string | null;
  /** False when the page opens the quote itself, as a policy does with a
   * signed handoff; the signed-in exchange still applies to its refusals. */
  openOnArrival: boolean;
  fetchView: (quoteId: string) => Promise<V>;
  fetchPresence?: (quoteId: string) => Promise<QuotePresence>;
  /** Why the quote refused this browser, while it is refused. */
  refusalReason: string | null;
  /** How the arrival ended. */
  onOutcome: (outcome: AccessOutcome<V>) => void;
  /** A signed-in exchange opened the quote. */
  onExchanged: (view: V) => void;
}

/**
 * Opens a quote for this browser once per arrival: presents the link or
 * reads the session cookie, then, when the quote refuses a signed-in
 * visitor for a reason an exchange of their own session can help, makes
 * that exchange once. `exchangePending` is true while the page should keep
 * waiting rather than show a refusal.
 *
 * A visitor who proves an email or phone on the re-entry screen is signed
 * in as whoever holds it, and the exchange is made once more for that
 * account. `signedInAfterReentry` tells the hook the sign-in came from the
 * screen, so `reentryRefused` is true when the exchange for it, or after a
 * re-entry link, left the quote closed: the proven channel reaches nobody
 * who may open it. A re-entry link that proved a channel reaching nobody
 * for this quote lands here signed out, and counts as refused from the
 * start.
 */
export function useQuoteAccess<V>(options: QuoteAccessOptions<V>): {
  exchangePending: boolean;
  reentryRefused: boolean;
  signedInAfterReentry: () => void;
} {
  const { quoteId, subject, linkToken, openOnArrival, refusalReason } = options;
  const { reportFailure } = useQuoteFlowAnalytics();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const { user: firebaseUser } = useFirebase();
  const getIdToken = useGetIdToken();
  const signedIn = user !== null && !userLoading;
  const location = useLocation();
  const [exchange, setExchange] = useState<
    "idle" | "trying" | "opened" | "refused"
  >("idle");
  const [afterReentry, setAfterReentry] = useState(() =>
    readReentryProof(location.state),
  );
  const [linkFoundNobody] = useState(() => readReentryNotFound(location.state));

  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A link is spent by its first presentation, so StrictMode's second pass
  // must not present it again; the exchange is guarded the same way.
  const openedFor = useRef<string | null>(null);
  const exchangedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!quoteId || !openOnArrival) return;
    if (openedFor.current === quoteId) return;
    openedFor.current = quoteId;

    const deps = {
      fetchView: () => latest.current.fetchView(quoteId),
      fetchPresence: latest.current.fetchPresence
        ? () => latest.current.fetchPresence!(quoteId)
        : undefined,
      stripLink: () => navigate(window.location.pathname, { replace: true }),
      report: reportFailure,
    };
    const opening = linkToken
      ? openWithLink({
          ...deps,
          mintLink: () =>
            mintQuoteSession(quoteId, { kind: "link", token: linkToken }),
        })
      : openWithCookie(deps);
    void opening.then((outcome) => {
      if (alive.current) latest.current.onOutcome(outcome);
    });
    // The arrival is chosen once, from the link captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, openOnArrival]);

  const due =
    refusalReason !== null &&
    shouldTrySignedInSession({
      subject,
      reason: refusalReason,
      signedIn,
      untried: exchange === "idle",
      afterReentry,
    });

  useEffect(() => {
    if (!due || !quoteId) return;
    if (exchangedFor.current === quoteId) return;
    exchangedFor.current = quoteId;
    setExchange("trying");
    void exchangeSignedInSession({
      exchange: () => requestSignedInSession(quoteId, getIdToken),
      fetchView: () => latest.current.fetchView(quoteId),
      report: reportFailure,
    }).then((view) => {
      if (!alive.current) return;
      if (view !== null) latest.current.onExchanged(view);
      setExchange(view === null ? "refused" : "opened");
    });
    // Keyed on its own due-ness: one exchange per arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due, quoteId]);

  // The exchange is for whoever is signed in: another account taking over
  // the browser gets its own.
  const accountId = firebaseUser?.uid ?? null;
  const exchangeAccount = useRef(accountId);
  useEffect(() => {
    if (exchangeAccount.current === accountId) return;
    exchangeAccount.current = accountId;
    exchangedFor.current = null;
    setExchange("idle");
  }, [accountId]);

  const signedInAfterReentry = useCallback(() => setAfterReentry(true), []);

  const exchangeMayHelp =
    refusalReason !== null &&
    signedInExchangeCanHelp(subject, refusalReason, afterReentry);
  return {
    exchangePending:
      exchangeMayHelp && (userLoading || due || exchange === "trying"),
    reentryRefused: linkFoundNobody || (afterReentry && exchange === "refused"),
    signedInAfterReentry,
  };
}
